import { useId, type ReactNode } from "react";
import { Glyph } from "./Icons";

/**
 * Scelte con input NATIVI (radio = una sola scelta, checkbox = più scelte),
 * ridisegnati via CSS: tastiera, lettori di schermo e invio del form funzionano
 * da soli. Nessun avanzamento automatico al `change`: con le frecce si scorrono
 * le opzioni, si va avanti con «Continua».
 */

export interface ChipOption {
  value: string;
  label: string;
}

interface ChipGroupProps {
  legend: ReactNode;
  optional?: string;
  options: ChipOption[];
  multiple?: boolean;
  value: readonly string[];
  onChange: (value: string, checked: boolean) => void;
  hint?: ReactNode;
  className?: string;
}

/** Domanda a chip dentro un `fieldset`, con `name` unico per istanza (pagina e widget convivono). */
export function ChipGroup({ legend, optional, options, multiple = false, value, onChange, hint, className }: ChipGroupProps) {
  const uid = useId();
  const hintId = `${uid}-hint`;
  return (
    <fieldset className={`rq-fieldset rq-group ${className ?? ""}`} aria-describedby={hint ? hintId : undefined}>
      <legend className="rq-legend">
        {legend}
        {optional && <span className="rq-legend__opt"> {optional}</span>}
      </legend>
      {hint && <p id={hintId} className="rq-help rq-help--q">{hint}</p>}
      <div className="rq-chips">
        {options.map((o) => {
          const id = `${uid}-${o.value}`;
          return (
            <label key={o.value} htmlFor={id} className="rq-chip">
              <input
                id={id}
                className="rq-chip__input"
                type={multiple ? "checkbox" : "radio"}
                name={`${uid}-q`}
                value={o.value}
                checked={value.includes(o.value)}
                onChange={(e) => onChange(o.value, e.currentTarget.checked)}
              />
              <Glyph name="check" className="rq-chip__tick" />
              <span className="rq-chip__text">{o.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

interface TileProps {
  id: string;
  name: string;
  value: string;
  checked: boolean;
  label: string;
  desc: string;
  icon: ReactNode;
  onChange: () => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  describedBy?: string;
}

/** Tessera di un argomento: tutta la tessera è la `<label>` del radio. */
export function IntentTile({ id, name, value, checked, label, desc, icon, onChange, inputRef, describedBy }: TileProps) {
  const labelId = `${id}-l`;
  const descId = `${id}-d`;
  return (
    <label htmlFor={id} className="rq-tile" data-gb={`intent-${value}`}>
      {icon}
      <span className="rq-tile__text">
        <span id={labelId} className="rq-tile__label">{label}</span>
        <span id={descId} className="rq-tile__desc">{desc}</span>
      </span>
      <input
        ref={inputRef}
        id={id}
        className="rq-control"
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        aria-labelledby={labelId}
        aria-describedby={[descId, describedBy].filter(Boolean).join(" ")}
      />
    </label>
  );
}
